import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Clock, Users, CheckCircle, AlertTriangle, Calendar, Target, Save, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ProjectContext } from "../ProjectStructureTab";
import { format } from "date-fns";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ReactFlow, Controls, Background, Node, Edge, ConnectionLineType, useNodesState, useEdgesState, addEdge, MarkerType, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { safeResourceIdToString, safeUuidSubstring, logResourceMatching } from "../resource-assignment/utils/resourceIdMapping";

interface TaskAllocationAnalysisProps {
  taskAllocations: any[];
  project: any;
}

const TaskAllocationAnalysis = ({
  taskAllocations,
  project
}: TaskAllocationAnalysisProps) => {
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
  const [showErrorDialog, setShowErrorDialog] = React.useState(false);
  const [dialogMessage, setDialogMessage] = React.useState("");
  const [isOrgChartOpen, setIsOrgChartOpen] = React.useState(false);
  const {
    selectedProject
  } = React.useContext(ProjectContext);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowInstance = React.useRef<any>(null);

  // Create hierarchical edges with proper connector positioning
  const createHierarchicalEdges = React.useCallback((flowNodes: Node[]) => {
    const hierarchicalEdges: Edge[] = [];

    // Group nodes by role type
    const nodesByRoleType: Record<string, Node[]> = {
      'project-director': [],
      'project-manager': [],
      'team-lead': [],
      'team-member': []
    };
    flowNodes.forEach(node => {
      const roleType = node.data.role_type as string || 'team-member';
      if (nodesByRoleType[roleType]) {
        nodesByRoleType[roleType].push(node);
      }
    });
    const maxSubordinatesPerSupervisor = 5;

    // Connect team members (top) to team leads (bottom)
    if (nodesByRoleType['team-member'].length > 0 && nodesByRoleType['team-lead'].length > 0) {
      const teamMembers = nodesByRoleType['team-member'];
      const teamLeads = nodesByRoleType['team-lead'];
      teamMembers.forEach((member, index) => {
        const leadIndex = Math.floor(index / maxSubordinatesPerSupervisor) % teamLeads.length;
        const targetLead = teamLeads[leadIndex];
        hierarchicalEdges.push({
          id: `edge-member-${member.id}-lead-${targetLead.id}`,
          source: member.id,
          sourceHandle: 'top',
          target: targetLead.id,
          targetHandle: 'bottom',
          type: 'smoothstep',
          style: {
            stroke: '#9370DB',
            strokeWidth: 2
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#9370DB'
          }
        });
      });
    }

    // Connect team leads (top) to project managers (bottom)
    if (nodesByRoleType['team-lead'].length > 0 && nodesByRoleType['project-manager'].length > 0) {
      const teamLeads = nodesByRoleType['team-lead'];
      const projectManagers = nodesByRoleType['project-manager'];
      teamLeads.forEach((lead, index) => {
        const managerIndex = Math.floor(index / maxSubordinatesPerSupervisor) % projectManagers.length;
        const targetManager = projectManagers[managerIndex];
        hierarchicalEdges.push({
          id: `edge-lead-${lead.id}-manager-${targetManager.id}`,
          source: lead.id,
          sourceHandle: 'top',
          target: targetManager.id,
          targetHandle: 'bottom',
          type: 'smoothstep',
          style: {
            stroke: '#8A2BE2',
            strokeWidth: 2
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#8A2BE2'
          }
        });
      });
    }

    // Connect project managers (top) to project directors (bottom)
    if (nodesByRoleType['project-manager'].length > 0 && nodesByRoleType['project-director'].length > 0) {
      const projectManagers = nodesByRoleType['project-manager'];
      const projectDirectors = nodesByRoleType['project-director'];
      projectManagers.forEach((manager, index) => {
        const directorIndex = Math.floor(index / maxSubordinatesPerSupervisor) % projectDirectors.length;
        const targetDirector = projectDirectors[directorIndex];
        hierarchicalEdges.push({
          id: `edge-manager-${manager.id}-director-${targetDirector.id}`,
          source: manager.id,
          sourceHandle: 'top',
          target: targetDirector.id,
          targetHandle: 'bottom',
          type: 'smoothstep',
          style: {
            stroke: '#6A5ACD',
            strokeWidth: 2
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#6A5ACD'
          }
        });
      });
    }
    return hierarchicalEdges;
  }, []);

  // ENHANCED: React Flow visualization with UUID consistency validation
  React.useEffect(() => {
    if (!taskAllocations || taskAllocations.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    console.log('🔍 TASK ALLOCATION - Processing task allocations for org chart:', {
      totalAllocations: taskAllocations.length,
      firstAllocation: taskAllocations[0]
    });

    // CRITICAL FIX: Collect all resources with enhanced UUID consistency validation
    const allResources: any[] = [];
    taskAllocations.forEach((allocation, allocIndex) => {
      console.log(`📋 Processing allocation ${allocIndex + 1}: "${allocation.taskName}" with ${allocation.assignedResources?.length || 0} resources`);
      
      if (allocation.assignedResources && Array.isArray(allocation.assignedResources)) {
        allocation.assignedResources.forEach((resource: any, resIndex: number) => {
          // ENHANCED: Safe UUID handling with validation
          const resourceId = safeResourceIdToString(resource.resource_id);
          const resourceUuid = resourceId; // The resource_id should now be the UUID
          
          console.log(`  Resource ${resIndex + 1}: ${resource.name} (UUID: ${safeUuidSubstring(resourceUuid)}, role_type: ${resource.role_type})`);
          
          allResources.push({
            ...resource,
            id: `${resource.name}-${resource.role}`,
            role_type: resource.role_type || 'team-member',
            uuid: resourceUuid,
            resource_id_safe: resourceId // Store safe string version
          });
        });
      }
    });

    // Enhanced deduplication based on UUID consistency
    const uniqueResources = allResources.filter((resource, index, self) => 
      index === self.findIndex(r => r.uuid === resource.uuid)
    );

    console.log('🎯 TASK ALLOCATION - UUID-consistent unique resources for org chart:', {
      totalResources: allResources.length,
      uniqueResources: uniqueResources.length,
      deduplicationSavings: allResources.length - uniqueResources.length,
      byRoleType: {
        'project-director': uniqueResources.filter(r => r.role_type === 'project-director').length,
        'project-manager': uniqueResources.filter(r => r.role_type === 'project-manager').length,
        'team-lead': uniqueResources.filter(r => r.role_type === 'team-lead').length,
        'team-member': uniqueResources.filter(r => r.role_type === 'team-member').length
      },
      sampleUuids: uniqueResources.slice(0, 3).map(r => ({ 
        name: r.name, 
        uuid: safeUuidSubstring(r.uuid),
        role_type: r.role_type 
      }))
    });

    // Log resource matching for debugging consistency
    logResourceMatching('Task Allocation Org Chart', uniqueResources.map(r => ({
      uuid: r.uuid,
      resource_id: parseInt(r.resource_id_safe) || 0,
      name: r.name,
      role: r.role,
      role_type: r.role_type
    })), { 
      source: 'TaskAllocationAnalysis',
      totalAllocations: taskAllocations.length 
    });

    // Group by role type
    const nodesByRoleType: Record<string, any[]> = {
      'project-director': [],
      'project-manager': [],
      'team-lead': [],
      'team-member': []
    };
    uniqueResources.forEach(resource => {
      const roleType = resource.role_type || 'team-member';
      if (nodesByRoleType[roleType]) {
        nodesByRoleType[roleType].push(resource);
      } else {
        nodesByRoleType['team-member'].push(resource);
      }
    });
    
    const ROLE_TYPE_SPACING = 200;
    const ROW_SPACING = 120;
    const NODE_WIDTH = 200;
    const NODES_PER_ROW = 8;
    let currentY = 50;
    const roleTypeStartPositions = {
      'project-director': currentY,
      'project-manager': 0,
      'team-lead': 0,
      'team-member': 0
    };

    // Calculate Y positions for each role type
    Object.keys(roleTypeStartPositions).forEach((roleType, index) => {
      if (index === 0) return;
      const prevRoleTypes = Object.keys(roleTypeStartPositions).slice(0, index);
      let maxY = roleTypeStartPositions[prevRoleTypes[0] as keyof typeof roleTypeStartPositions];
      prevRoleTypes.forEach(prevRoleType => {
        const prevNodes = nodesByRoleType[prevRoleType] || [];
        if (prevNodes.length > 0) {
          const rows = Math.ceil(prevNodes.length / NODES_PER_ROW);
          const prevRoleMaxY = roleTypeStartPositions[prevRoleType as keyof typeof roleTypeStartPositions] + (rows - 1) * ROW_SPACING;
          maxY = Math.max(maxY, prevRoleMaxY);
        }
      });
      roleTypeStartPositions[roleType as keyof typeof roleTypeStartPositions] = maxY + ROLE_TYPE_SPACING;
    });
    const flowNodes: Node[] = [];
    Object.entries(nodesByRoleType).forEach(([roleType, roleResources]) => {
      if (roleResources.length === 0) return;
      const startY = roleTypeStartPositions[roleType as keyof typeof roleTypeStartPositions];
      roleResources.forEach((resource, index) => {
        const rowIndex = Math.floor(index / NODES_PER_ROW);
        const positionInRow = index % NODES_PER_ROW;
        const totalRowWidth = Math.min(roleResources.length - rowIndex * NODES_PER_ROW, NODES_PER_ROW) * NODE_WIDTH;
        const startX = -totalRowWidth / 2;
        const positionX = startX + positionInRow * NODE_WIDTH + NODE_WIDTH / 2;
        const positionY = startY + rowIndex * ROW_SPACING;
        let nodeStyle: React.CSSProperties;
        switch (roleType) {
          case 'project-director':
            nodeStyle = {
              background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
              color: 'white',
              border: '3px solid #7C3AED',
              borderRadius: '12px',
              padding: '12px',
              width: '180px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              fontWeight: 'bold'
            };
            break;
          case 'project-manager':
            nodeStyle = {
              background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
              color: 'white',
              border: '3px solid #2563EB',
              borderRadius: '10px',
              padding: '10px',
              width: '160px',
              textAlign: 'center',
              boxShadow: '0 6px 24px rgba(59, 130, 246, 0.3)',
              fontWeight: '600'
            };
            break;
          case 'team-lead':
            nodeStyle = {
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: 'white',
              border: '2px solid #059669',
              borderRadius: '8px',
              padding: '10px',
              width: '140px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
              fontWeight: '500'
            };
            break;
          default:
            nodeStyle = {
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: 'white',
              border: '2px solid #D97706',
              borderRadius: '6px',
              padding: '8px',
              width: '120px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
              fontWeight: '400'
            };
            break;
        }
        flowNodes.push({
          id: resource.id,
          type: 'default',
          position: {
            x: positionX,
            y: positionY
          },
          data: {
            label: <div>
                <div className="text-sm font-medium">{resource.name}</div>
                <div className="text-xs">{resource.role}</div>
                <div className="text-xs opacity-70">{roleType.replace('-', ' ')}</div>
              </div>,
            role_type: roleType,
            resource: resource
          },
          style: nodeStyle,
          draggable: true,
          selectable: true,
          sourcePosition: Position.Top,
          targetPosition: Position.Bottom
        });
      });
    });
    setNodes(flowNodes);

    // Create hierarchical edges
    const hierarchicalEdges = createHierarchicalEdges(flowNodes);
    setEdges(hierarchicalEdges);
    setTimeout(() => {
      if (reactFlowInstance.current && flowNodes.length > 0) {
        reactFlowInstance.current.fitView({
          padding: 0.2,
          maxZoom: 1
        });
      }
    }, 100);
  }, [taskAllocations, createHierarchicalEdges]);

  // Function to get the correct dates from the selected project tasks
  const getCorrectTaskDates = (taskName: string) => {
    if (!selectedProject || !selectedProject.tasks) {
      return {
        startDate: format(new Date(), 'dd MMM yyyy'),
        endDate: format(new Date(), 'dd MMM yyyy')
      };
    }

    // Find matching task by name
    const matchingTask = selectedProject.tasks.find((task: any) => task.name === taskName || task.name.toLowerCase().includes(taskName.toLowerCase()));
    if (matchingTask) {
      return {
        startDate: matchingTask.startDate || format(new Date(), 'dd MMM yyyy'),
        endDate: matchingTask.endDate || format(new Date(), 'dd MMM yyyy')
      };
    }

    // Fallback to today's date if no match found
    return {
      startDate: format(new Date(), 'dd MMM yyyy'),
      endDate: format(new Date(), 'dd MMM yyyy')
    };
  };

  // Update task allocations with correct dates
  const updatedTaskAllocations = taskAllocations.map(allocation => {
    const correctDates = getCorrectTaskDates(allocation.taskName);
    return {
      ...allocation,
      startDate: correctDates.startDate,
      endDate: correctDates.endDate
    };
  });
  const totalTasks = updatedTaskAllocations.length;

  // ENHANCED: Calculate total assignments by role type with UUID consistency validation
  const roleTypeCounts = updatedTaskAllocations.reduce((counts, task) => {
    if (task.assignedResources && Array.isArray(task.assignedResources)) {
      task.assignedResources.forEach((resource: any) => {
        const roleType = resource.role_type || 'team-member';
        counts[roleType] = (counts[roleType] || 0) + 1;
      });
    }
    return counts;
  }, {} as Record<string, number>);
  
  const totalDirectors = roleTypeCounts['project-director'] || 0;
  const totalManagers = roleTypeCounts['project-manager'] || 0;
  const totalTeamLeads = roleTypeCounts['team-lead'] || 0;
  const totalTeamMembers = roleTypeCounts['team-member'] || 0;
  const totalResources = totalDirectors + totalManagers + totalTeamLeads + totalTeamMembers;
  const averageResourcesPerTask = totalTasks > 0 ? (totalResources / totalTasks).toFixed(1) : '0';

  // ENHANCED: Unique resource count for more accurate statistics
  const allUniqueResourceUuids = new Set();
  updatedTaskAllocations.forEach(allocation => {
    if (allocation.assignedResources && Array.isArray(allocation.assignedResources)) {
      allocation.assignedResources.forEach((resource: any) => {
        const resourceUuid = safeResourceIdToString(resource.resource_id);
        allUniqueResourceUuids.add(resourceUuid);
      });
    }
  });
  const totalUniqueResources = allUniqueResourceUuids.size;

  console.log('📊 TASK ALLOCATION - Enhanced statistics calculation:', {
    totalAssignments: totalResources,
    totalUniqueResources: totalUniqueResources,
    byRoleType: roleTypeCounts,
    resourceReuseRatio: totalResources > totalUniqueResources ? (totalResources / totalUniqueResources).toFixed(2) : '1.00'
  });
  
  const statusColors = {
    'optimal': 'bg-green-500/20 text-green-300 border-green-500/30',
    'warning': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    'critical': 'bg-red-500/20 text-red-300 border-red-500/30'
  };
  
  // NEW APPROACH: Helper function to update emails after assignments are created
  const updateAssignmentEmails = async (projectId: string, userId: string) => {
    console.log('🔧 UPDATING ASSIGNMENT EMAILS: Starting email update process');
    
    try {
      // Step 1: Get all resource assignments for this project that don't have emails
      const { data: assignments, error: assignmentsError } = await supabase
        .from('resource_assignment')
        .select('id, resource_id')
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .is('email', null);

      if (assignmentsError) {
        console.error('❌ Error fetching assignments:', assignmentsError);
        return;
      }

      if (!assignments || assignments.length === 0) {
        console.log('✅ No assignments need email updates');
        return;
      }

      console.log('📊 Found assignments needing email updates:', assignments.length);

      // Step 2: Get all resources for this project to create a lookup map
      const { data: resources, error: resourcesError } = await supabase
        .from('resources')
        .select('id, email')
        .eq('user_id', userId);

      if (resourcesError) {
        console.error('❌ Error fetching resources:', resourcesError);
        return;
      }

      if (!resources || resources.length === 0) {
        console.log('⚠️ No resources found for email lookup');
        return;
      }

      // Step 3: Create email lookup map
      const emailLookup = new Map();
      resources.forEach(resource => {
        if (resource.email) {
          emailLookup.set(resource.id, resource.email);
        }
      });

      console.log('📋 Created email lookup map with', emailLookup.size, 'entries');

      // Step 4: Update assignments with emails
      const updatePromises = assignments.map(async (assignment) => {
        const email = emailLookup.get(assignment.resource_id);
        
        if (email) {
          console.log(`📧 Updating assignment ${assignment.id} with email: ${email}`);
          
          const { error: updateError } = await supabase
            .from('resource_assignment')
            .update({ email })
            .eq('id', assignment.id);

          if (updateError) {
            console.error(`❌ Error updating assignment ${assignment.id}:`, updateError);
            return { success: false, assignmentId: assignment.id };
          }
          
          return { success: true, assignmentId: assignment.id };
        } else {
          console.warn(`⚠️ No email found for resource_id: ${assignment.resource_id}`);
          return { success: false, assignmentId: assignment.id, reason: 'No email found' };
        }
      });

      const updateResults = await Promise.all(updatePromises);
      const successCount = updateResults.filter(result => result.success).length;
      const failureCount = updateResults.length - successCount;

      console.log(`✅ EMAIL UPDATE COMPLETE: ${successCount} successful, ${failureCount} failed`);
      
      return { successCount, failureCount };
    } catch (error) {
      console.error('❌ Error in updateAssignmentEmails:', error);
      throw error;
    }
  };

  // ENHANCED: Fixed saveResourceAssignments with proper email handling
  const saveResourceAssignments = async () => {
    if (!project || updatedTaskAllocations.length === 0) {
      setDialogMessage("No task allocations to save");
      setShowErrorDialog(true);
      return;
    }

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        setDialogMessage("You must be logged in to save resource assignments");
        setShowErrorDialog(true);
        return;
      }

      console.log('🚀 FIXED IMPLEMENTATION: Starting resource assignment save');

      // Get all available resources
      const { data: availableResources, error: resourcesError } = await supabase
        .from('resources')
        .select('id, resource_id, name, role, role_type, capacity, rate')
        .eq('user_id', user.id);
        
      if (resourcesError) {
        console.error('❌ Error fetching resources:', resourcesError);
        setDialogMessage('Failed to fetch available resources');
        setShowErrorDialog(true);
        return;
      }

      // STEP 1: Clear existing assignments for this project
      console.log('🔄 STEP 1: Clearing existing assignments...');
      
      const { error: deleteError } = await supabase
        .from('resource_assignment')
        .delete()
        .eq('project_id', project.id)
        .eq('user_id', user.id);
        
      if (deleteError) {
        console.error('❌ Error deleting existing assignments:', deleteError);
        setDialogMessage('Failed to delete existing assignments');
        setShowErrorDialog(true);
        return;
      }

      console.log('✅ Existing assignments cleared successfully');

      // STEP 2: Get project tasks for duplication
      const projectTasks = selectedProject?.tasks || [];
      console.log('📋 Project tasks for duplication:', {
        totalTasks: projectTasks.length,
        taskNames: projectTasks.map((task: any) => task.name)
      });

      if (projectTasks.length === 0) {
        setDialogMessage('No project tasks found. Please ensure your project has tasks defined.');
        setShowErrorDialog(true);
        return;
      }

      // STEP 3: Collect unique resources from the first task allocation
      const firstTaskAllocation = updatedTaskAllocations[0];
      if (!firstTaskAllocation || !firstTaskAllocation.assignedResources) {
        setDialogMessage('No assigned resources found in AI analysis');
        setShowErrorDialog(true);
        return;
      }

      const uniqueResourcesMap = new Map();
      firstTaskAllocation.assignedResources.forEach((resource: any) => {
        const resourceUuid = resource.resource_id;
        if (!uniqueResourcesMap.has(resourceUuid)) {
          uniqueResourcesMap.set(resourceUuid, {
            uuid: resourceUuid,
            name: resource.name,
            role: resource.role,
            role_type: resource.role_type || 'team-member',
            rate: resource.rate ?? null,
            allocation: resource.allocation || 100
          });
        }
      });

      const uniqueResources = Array.from(uniqueResourcesMap.values());
      
      console.log('🎯 Unique resources for duplication:', {
        total: uniqueResources.length,
        byRoleType: {
          'project-director': uniqueResources.filter(r => r.role_type === 'project-director').length,
          'project-manager': uniqueResources.filter(r => r.role_type === 'project-manager').length,
          'team-lead': uniqueResources.filter(r => r.role_type === 'team-lead').length,
          'team-member': uniqueResources.filter(r => r.role_type === 'team-member').length
        }
      });

      // STEP 4: Create assignments for ALL tasks WITHOUT EMAIL (will be updated later)
      const allResourceAssignments = [];
      const matchingResults = {
        uuidMatches: 0,
        nameRoleMatches: 0,
        nameMatches: 0,
        roleTypeMatches: 0,
        unmatched: [] as any[]
      };
      
      for (const task of projectTasks) {
        console.log(`🔄 Creating assignments for task: "${task.name}"`);
        
        for (const resource of uniqueResources) {
          let matchingResource = null;
          let matchType = '';
          
          // Strategy 1: Direct UUID match
          matchingResource = availableResources?.find(r => r.id === resource.uuid);
          if (matchingResource) {
            matchType = 'uuid';
            if (task === projectTasks[0]) matchingResults.uuidMatches++;
          }
          
          // Strategy 2: Exact match by name AND role
          if (!matchingResource) {
            matchingResource = availableResources?.find(r => 
              r.name === resource.name && r.role === resource.role
            );
            if (matchingResource) {
              matchType = 'nameRole';
              if (task === projectTasks[0]) matchingResults.nameRoleMatches++;
            }
          }
          
          // Strategy 3: Match by name only
          if (!matchingResource) {
            matchingResource = availableResources?.find(r => r.name === resource.name);
            if (matchingResource) {
              matchType = 'name';
              if (task === projectTasks[0]) matchingResults.nameMatches++;
            }
          }
          
          // Strategy 4: Match by role_type
          if (!matchingResource) {
            matchingResource = availableResources?.find(r => r.role_type === resource.role_type);
            if (matchingResource) {
              matchType = 'roleType';
              if (task === projectTasks[0]) matchingResults.roleTypeMatches++;
            }
          }

          if (matchingResource) {
            // NOTE: No email field set here - will be updated in separate step
            allResourceAssignments.push({
              user_id: user.id,
              project_id: project.id,
              task_id: task.name,
              resource_id: matchingResource.id, // Use the UUID from the resources table
              name: resource.name,
              role: resource.role,
              role_type: resource.role_type,
              rate: matchingResource.rate ?? null,
              start_date: task.startDate || new Date().toISOString().split('T')[0],
              end_date: task.endDate || new Date().toISOString().split('T')[0],
              duration: task.duration || 30,
              allocated_percentage: resource.allocation,
              utilization_percentage: resource.allocation,
              allocation_hrs: matchingResource.capacity || '8'
              // email: null - will be updated in next step
            });
          } else if (task === projectTasks[0]) {
            matchingResults.unmatched.push({
              uuid: resource.uuid,
              name: resource.name,
              role: resource.role,
              role_type: resource.role_type
            });
          }
        }
      }

      // STEP 5: Save all assignments WITHOUT emails first
      const expectedTotal = uniqueResources.length * projectTasks.length;
      console.log('🎯 Assignment creation results:', {
        uniqueResourcesFromFirstTask: uniqueResources.length,
        totalProjectTasks: projectTasks.length,
        expectedTotalAssignments: expectedTotal,
        actualAssignmentsCreated: allResourceAssignments.length,
        duplicationSuccess: allResourceAssignments.length === expectedTotal,
        matchingBreakdown: matchingResults
      });

      if (allResourceAssignments.length !== expectedTotal) {
        const shortfall = expectedTotal - allResourceAssignments.length;
        console.warn(`⚠️ DUPLICATION SHORTFALL: Expected ${expectedTotal} assignments, got ${allResourceAssignments.length} (missing ${shortfall})`);
        
        const errorMessage = `Duplication incomplete: Expected ${expectedTotal} assignments (${uniqueResources.length} resources × ${projectTasks.length} tasks), but only ${allResourceAssignments.length} were created. ${matchingResults.unmatched.length} resources could not be matched.`;
        setDialogMessage(errorMessage);
        setShowErrorDialog(true);
        return;
      }

      if (allResourceAssignments.length > 0) {
        const { error: insertError } = await supabase
          .from('resource_assignment')
          .insert(allResourceAssignments);
          
        if (insertError) {
          console.error('❌ Error saving resource assignments:', insertError);
          setDialogMessage('Failed to save resource assignments: ' + insertError.message);
          setShowErrorDialog(true);
          return;
        }
        
        console.log('✅ Resource assignments saved successfully');

        // STEP 6: NEW APPROACH - Update emails using separate lookup
        console.log('🔧 STEP 6: Updating assignment emails using resource lookup...');
        
        const emailUpdateResults = await updateAssignmentEmails(project.id, user.id);
        
        // STEP 7: Success message
        const baseMessage = `Successfully assigned ${uniqueResources.length} resources across all ${projectTasks.length} project tasks, creating ${allResourceAssignments.length} total resource assignments.`;
        //const emailMessage = emailUpdateResults 
        //  ? ` Emails updated: ${emailUpdateResults.successCount} successful, ${emailUpdateResults.failureCount} failed.`
        //  : '';
        
        //const successMessage = baseMessage + emailMessage;
        const successMessage = baseMessage;
        
        console.log('🎉 COMPLETE SUCCESS WITH EMAIL UPDATES:', {
          totalAssignmentsSaved: allResourceAssignments.length,
          resourcesPerTask: uniqueResources.length,
          totalTasks: projectTasks.length,
          emailUpdates: emailUpdateResults
        });
        
        setDialogMessage(successMessage);
        setShowSuccessDialog(true);
      } else {
        setDialogMessage("No matching resources found. Please ensure resources exist in your resource pool with correct UUIDs and matching names/roles.");
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('❌ Error in resource assignment save:', error);
      setDialogMessage('Failed to save resource assignments');
      setShowErrorDialog(true);
    }
  };

  return <>
      <div className="space-y-4 col-span-12">
        {/* Statistics Card */}
        <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary shadow-2xl shadow-primary/20">
          <CardHeader className="bg-secondary/50 border-b border-primary/30">
            
            <div className="flex justify-between items-center">

              <CardTitle className="text-white flex items-center gap-3 text-xl w-full py-2">


              
                <div className="w-[90%]">
                  <span className="bg-gradient-to-r from-primary to-green-400 bg-clip-text text-transparent font-bold">
                    AI Task Allocation Analysis
                  </span>
                  <div className="text-sm text-gray-300 font-normal">Resource Distribution & Task Management</div>
                </div>

                {/* Save Button */}
                {updatedTaskAllocations.length > 0 && <div className="flex gap-0 mt-0">
                  <Button onClick={saveResourceAssignments} className="bg-green-600 hover:bg-green-700 text-white font-medium">
                    Apply AI assigned resources to project
                  </Button>
                </div>} 

              </CardTitle>
            
            </div>
            
            {/* Enhanced Task Statistics with UUID-consistent counts */}
            <div className="bg-secondary/50 p-5 rounded-xl border border-primary/30 mt-4 backdrop-blur-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
                <div className="text-center bg-gradient-to-br from-primary/20 to-blue-600/20 p-4 rounded-lg border border-primary/30">
                  <div className="text-3xl font-bold text-primary mb-1">{totalTasks}</div>
                  <div className="text-xs text-gray-300 uppercase tracking-wide">Total Project Tasks</div>
                </div>
                <div className="text-center bg-gradient-to-br from-green-600/20 to-emerald-600/20 p-4 rounded-lg border border-green-500/30">
                  <div className="text-3xl font-bold text-green-300 mb-1">{totalResources}</div>
                  <div className="text-xs text-gray-300 uppercase tracking-wide">Total Assignments</div>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-600/20 to-pink-600/20 p-4 rounded-lg border border-purple-500/30">
                  <div className="text-3xl font-bold text-purple-300 mb-1">{totalUniqueResources}</div>
                  <div className="text-xs text-gray-300 uppercase tracking-wide">Unique Resources</div>
                </div>
                <div className="text-center bg-gradient-to-br from-blue-600/20 to-cyan-600/20 p-4 rounded-lg border border-blue-500/30">
                  <div className="text-3xl font-bold text-blue-300 mb-1">{averageResourcesPerTask}</div>
                  <div className="text-xs text-gray-300 uppercase tracking-wide">Avg Resources/Task</div>
                </div>
              </div>

            </div>

          </CardHeader>
        </Card>

        {/* React Flow Organizational Chart */}
        {nodes.length > 0 && (
          <Collapsible open={isOrgChartOpen} onOpenChange={setIsOrgChartOpen}>
            <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary shadow-2xl shadow-primary/20">
              <CollapsibleTrigger asChild>
                <CardHeader className="bg-secondary/50 border-b border-primary/30 cursor-pointer hover:bg-primary/5">
                  <div className="flex justify-between items-center w-full">
                    <CardTitle className="text-white flex items-center gap-3 text-xl">

                      <span className="bg-gradient-to-r from-primary to-green-400 bg-clip-text text-transparent font-bold">
                        Resource Organization Chart ({totalUniqueResources} unique resources)
                      </span>
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-primary-light">Click to expand or collapse</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 text-primary-light ${isOrgChartOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="p-0">
                  <div style={{
                height: 600,
                width: '100%'
              }}>
                    <ReactFlow 
                      ref={reactFlowInstance} 
                      nodes={nodes} 
                      edges={edges} 
                      onNodesChange={onNodesChange} 
                      onEdgesChange={onEdgesChange} 
                      onConnect={params => setEdges(eds => addEdge(params, eds))} 
                      onInit={instance => {
                        reactFlowInstance.current = instance;
                        setTimeout(() => {
                          instance.fitView({
                            padding: 0.2,
                            maxZoom: 1
                          });
                        }, 100);
                      }} 
                      fitView={false} 
                      connectionLineType={ConnectionLineType.SmoothStep} 
                      nodesDraggable={true} 
                      nodesConnectable={false} 
                      elementsSelectable={true} 
                      panOnDrag={true} 
                      zoomOnScroll={true} 
                      preventScrolling={false} 
                      defaultViewport={{
                        x: 0,
                        y: 0,
                        zoom: 1
                      }} 
                      minZoom={0.1} 
                      maxZoom={2}
                    >
                      <Controls />
                      <Background color="#aaa" gap={16} />
                    </ReactFlow>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Task Details */}
        <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary shadow-2xl shadow-primary/20">
          <CardContent className="max-h-[700px] overflow-y-auto p-6">
            <div className="space-y-6">
              {updatedTaskAllocations.map((allocation, index) => (
                <div key={index} className="bg-gradient-to-br from-primary/10 via-secondary/20 to-primary/5 p-6 rounded-xl border-2 border-primary/20 backdrop-blur-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                        {allocation.taskName}
                      </h3>
                      <p className="text-gray-300 text-sm mb-3 leading-relaxed">
                        {allocation.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2 bg-primary/10 px-3 py-1 rounded-lg border border-primary/20">
                          <Calendar className="h-4 w-4 text-primary" />
                          <span className="text-white font-medium">
                            {allocation.startDate} - {allocation.endDate}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-blue-500/10 px-3 py-1 rounded-lg border border-blue-500/20">
                          <Clock className="h-4 w-4 text-blue-400" />
                          <span className="text-white font-medium">{allocation.duration} days</span>
                        </div>
                      </div>
                    </div>
                    <Badge className={`px-3 py-1 text-sm font-semibold ${statusColors[allocation.status] || statusColors.optimal}`}>
                      {allocation.status === 'optimal' && <CheckCircle className="h-4 w-4 mr-1" />}
                      {allocation.status === 'warning' && <AlertTriangle className="h-4 w-4 mr-1" />}
                      {allocation.status === 'critical' && <AlertTriangle className="h-4 w-4 mr-1" />}
                      {allocation.status.charAt(0).toUpperCase() + allocation.status.slice(1)}
                    </Badge>
                  </div>

                  <div className="border-t border-primary/20 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-white font-semibold">Assigned Resources ({allocation.assignedResources?.length || 0})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(allocation.assignedResources || []).map((resource: any, resourceIndex: number) => (
                        <div key={resourceIndex} className="bg-gradient-to-r from-secondary/30 to-primary/10 p-3 rounded-lg border border-primary/20">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-white font-medium text-sm truncate">{resource.name}</h4>
                            <span className="text-xs text-primary font-mono bg-primary/10 px-2 py-1 rounded">
                              {resource.allocation}%
                            </span>
                          </div>
                          <p className="text-gray-300 text-xs mb-2">{resource.role}</p>
                          {resource.role_type && <p className="text-blue-300 text-xs mb-2 capitalize">{resource.role_type.replace('-', ' ')}</p>}
                          {resource.resource_id && <p className="text-purple-300 text-xs mb-2 font-mono">UUID: {safeUuidSubstring(resource.resource_id, 8)}...</p>}
                          <div className="flex items-center justify-between">
                            <span className="text-green-300 text-xs font-semibold">
                              ${resource.rate}/hr
                            </span>
                            <div className="w-16 bg-secondary/50 rounded-full h-2">
                              <div className="bg-gradient-to-r from-primary to-green-400 h-2 rounded-full transition-all duration-300" style={{
                                width: `${resource.allocation}%`
                              }}></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {allocation.recommendations && (
                    <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-300 text-sm leading-relaxed italic">
                          {allocation.recommendations}
                        </p>
                      </div>
                    </div>
                  )}

                  {allocation.conflicts && allocation.conflicts.length > 0 && (
                    <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-red-300 text-sm font-medium mb-1">Conflicts Detected:</p>
                          <ul className="text-red-200 text-xs space-y-1">
                            {allocation.conflicts.map((conflict: string, conflictIndex: number) => (
                              <li key={conflictIndex}>• {conflict}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {updatedTaskAllocations.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-gradient-to-br from-primary/20 to-green-600/20 p-8 rounded-2xl border-2 border-primary/30">
                  <Target className="h-16 w-16 text-primary mx-auto mb-4 animate-bounce" />
                  <p className="text-gray-300 text-lg font-semibold">No task allocations available yet.</p>
                  <p className="text-gray-400 text-sm mt-2">Run the AI analysis to get detailed task allocation recommendations.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Success</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowSuccessDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowErrorDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
};
export default TaskAllocationAnalysis;
